import { Link } from 'react-router-dom';
import { Bookmark, Play, Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/common/EmptyState';
import { SkeletonCard } from '@/components/common/SkeletonCard';
import { toast } from 'sonner';
import { getSavedSearches, deleteSavedSearch } from '@/services/consumerApi';

export default function SavedSearches() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: getSavedSearches,
  });

  const searches = data?.results || [];

  const deleteMut = useMutation({
    mutationFn: deleteSavedSearch,
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['saved-searches'] });
      const prev = qc.getQueryData(['saved-searches']);
      qc.setQueryData(['saved-searches'], (old) => ({
        ...old,
        results: (old?.results || []).filter((s) => s.id !== id),
      }));
      return { prev };
    },
    onError: (_, __, ctx) => {
      qc.setQueryData(['saved-searches'], ctx.prev);
      toast.error('Could not remove search');
    },
    onSuccess: () => toast('Search removed'),
  });

  const toQuery = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach((val) => params.append(k, val));
      else if (v !== null && v !== undefined && v !== '') params.set(k, v);
    });
    return '/listings?' + params.toString();
  };

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Saved searches</h1>
        <p className="text-sm text-slate-600">Re-run searches and manage alerts.</p>
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-slate-900">Saved searches</h1>
      <p className="text-sm text-slate-600">Re-run searches and manage alerts.</p>
      <div className="mt-6">
        {searches.length === 0 ? (
          <EmptyState
            icon={Bookmark}
            title="No saved searches"
            description="Save a search from the listings page to get alerts when new homes match."
            actionLabel="Search homes"
            onAction={() => (window.location.href = '/listings')}
          />
        ) : (
          <div className="space-y-3">
            {searches.map((s) => (
              <div key={s.id} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between" data-testid="saved-search-row">
                <div><p className="font-medium text-slate-900">{s.name}</p></div>
                <div className="flex items-center gap-3">
                  <Button asChild size="sm" className="rounded-xl">
                    <Link to={toQuery(s.filters)}><Play size={14} className="mr-1" />Run</Link>
                  </Button>
                  <button
                    onClick={() => deleteMut.mutate(s.id)}
                    disabled={deleteMut.isPending}
                    className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                    aria-label="Delete"
                    data-testid="saved-search-delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
